import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase, apiFetch } from '../../lib/supabase';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, STATUS_STYLE, btn, inp } from '../../config/styles';
import { makeProduct, calcOverallScore, calcSectionScore, buildProductGroups, uid, TWO_DIM_VARIANT_TYPE } from '../../utils/products';
import { syncImagesFields } from '../products/forms';
import { isVideoFile, mediaBucketFor } from '../../utils/products';
import { matchProductsInFilename } from '../../utils/mediaMatch';
import { Ring } from '../ui/shared';
import { ImportModal, DuplicateModal } from '../products/modals';

// ─────────────────────────────────────────────────────────────
// CONCURRENCY HELPER
// ─────────────────────────────────────────────────────────────

async function runConcurrent(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// ─────────────────────────────────────────────────────────────
// PRODUCT GROUP
// ─────────────────────────────────────────────────────────────

function ProductGroup({ groupKey, items, isManager, onEdit, onDelete, selected, onSelect, onSyncPrice, groupLabel }) {
  const [collapsed, setCollapsed] = useState(false);
  const [syncOpen, setSyncOpen]   = useState(false);
  const [syncPrice, setSyncPrice] = useState('');

  const prices      = [...new Set(items.map(p => p.basePrice).filter(Boolean))];
  const sharedPrice = prices.length === 1 ? prices[0] : null;

  const applySync = () => {
    const val = parseFloat(syncPrice);
    if (isNaN(val) || val <= 0) return;
    onSyncPrice(items.map(p => p.id), syncPrice);
    setSyncOpen(false);
  };

  return (
    <div style={{ border: `1px solid ${C.e8}`, borderRadius: 10, overflow: 'hidden', marginBottom: 2 }}>
      {/* Group header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: C.fa, cursor: 'pointer' }}
        onClick={() => setCollapsed(c => !c)}>
        <span style={{ fontSize: 12, color: C.muted, lineHeight: 1, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▾</span>
        <span style={{ fontFamily: F.display, fontSize: 14, color: C.text, flex: 1 }}>{groupKey}</span>
        {((!sharedPrice && prices.length > 1) || /co[\s-]?ord/i.test(groupKey))
          ? <span style={{ fontSize: 11, color: '#6d28d9', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: 10, padding: '2px 8px' }}>Co-ord set · {items.length} pieces</span>
          : <span style={{ fontSize: 11, color: C.muted, background: C.white, border: `1px solid ${C.e8}`, borderRadius: 10, padding: '2px 8px' }}>{items.length} {groupLabel}{items.length !== 1 ? 's' : ''}</span>
        }
        {isManager && (
          <div onClick={e => { e.stopPropagation(); setSyncOpen(o => !o); setSyncPrice(sharedPrice || ''); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button style={{ ...btn('ghost', { fontSize: 11, padding: '3px 10px' }) }}>
              {sharedPrice ? `£${sharedPrice} · Sync price` : 'Sync price'}
            </button>
          </div>
        )}
      </div>

      {/* Sync price inline panel */}
      {syncOpen && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#fafafa', borderTop: `1px solid ${C.e8}` }}>
          <span style={{ fontSize: 12, color: C.text2 }}>Set price for all {items.length} {groupLabel}s:</span>
          <span style={{ fontSize: 12, color: C.muted }}>£</span>
          <input autoFocus style={{ ...inp({ width: 80, padding: '4px 8px', fontSize: 12 }) }} type="number" step="0.01"
            value={syncPrice} onChange={e => setSyncPrice(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setSyncOpen(false)} />
          <button style={btn('primary', { fontSize: 12, padding: '5px 14px' })} onClick={applySync}>Apply</button>
          <button style={btn('ghost', { fontSize: 12, padding: '5px 10px' })} onClick={() => setSyncOpen(false)}>Cancel</button>
        </div>
      )}

      {/* Product rows */}
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', background: C.white }}>
          {items.map((p) => (
            <div key={p.id} style={{ borderRadius: 8, overflow: 'hidden' }}>
              <ProductRow p={p} isManager={isManager}
                onEdit={onEdit} onDelete={onDelete}
                selected={selected.has(p.id)} onSelect={onSelect}
                onPriceChange={(id, price) => onSyncPrice([id], price)}
                inGroup />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PRODUCT ROW
// ─────────────────────────────────────────────────────────────

function ProductRow({ p, isManager, onEdit, onDelete, selected, onSelect, onPriceChange, inGroup = false }) {
  const tenantConfig = useTenantConfig();
  const { deptIds, deptLabels } = tenantConfig;
  const [hov, setHov]           = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceVal, setPriceVal]   = useState('');
  const score       = calcOverallScore(p, tenantConfig);
  const allApproved = deptIds.every(d => p.sectionStatus[d] === 'approved');
  const hasReview   = deptIds.some(d => p.sectionStatus[d] === 'review');

  const applyPrice = () => {
    const val = parseFloat(priceVal);
    if (isNaN(val) || val <= 0) return;
    onPriceChange(p.id, priceVal);
    setPriceOpen(false);
  };

  return (
    <div style={{ borderRadius: 10, border: `1px solid ${priceOpen ? C.d0 : selected || hov ? C.d0 : C.e8}`, overflow: 'hidden', transition: 'all 0.15s' }}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 14px',
          background: selected ? '#f5f5f5' : hov ? C.fa : C.white,
          transition: 'all 0.15s',
        }}
      >
      {isManager && (
        <input type="checkbox" checked={!!selected}
          onChange={e => { e.stopPropagation(); onSelect(p.id, e.target.checked); }}
          onClick={e => e.stopPropagation()}
          style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
      )}

      <div onClick={() => onEdit(p)} style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, cursor: 'pointer' }}>
        {p.imageUrl ? (
          <img src={p.imageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: `1px solid ${C.e8}` }} />
        ) : (
          <div style={{ width: 48, height: 48, background: C.f4, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.e8}` }}>
            <span style={{ fontSize: 20, color: C.e8 }}>□</span>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.display, fontSize: 15, color: p.title ? C.text : C.ph, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.title || 'Untitled product'}
          </div>
          <div style={{ fontFamily: F.mono, fontSize: 11, color: C.muted, marginTop: 2 }}>
            {p.skuBase || '-'}
            {p.variants?.length > 0 && (
              <span style={{ marginLeft: 8, color: C.ph }}>{p.variants.length} variant{p.variants.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {deptIds.map(d => {
            const s = STATUS_STYLE[p.sectionStatus[d] || 'empty'];
            return <div key={d} title={`${deptLabels[d]}: ${s.label}`} style={{ width: 8, height: 8, borderRadius: '50%', background: s.fg }} />;
          })}
        </div>

        {!inGroup && p.basePrice && isManager && onPriceChange && (
          <button onClick={e => { e.stopPropagation(); setPriceOpen(o => !o); setPriceVal(p.basePrice); }}
            style={{ ...btn('ghost', { fontSize: 11, padding: '3px 10px' }), flexShrink: 0 }}>
            £{p.basePrice} · Set price
          </button>
        )}

        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, width: 72 }}>
          {allApproved && (
            <span style={{ fontSize: 11, color: '#065f46', background: '#d1fae5', padding: '3px 8px', borderRadius: 20, fontWeight: 500 }}>Ready</span>
          )}
          {!allApproved && hasReview && (
            <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: 20, fontWeight: 500 }}>Review</span>
          )}
          {p.shopifyConnections && Object.keys(p.shopifyConnections).length > 0 && (() => {
            const conns = Object.values(p.shopifyConnections);
            const live   = conns.some(c => c.status === 'active');
            const drafted = conns.some(c => c.status === 'draft');
            return (
              <span title={`In Shopify: ${conns.map(c => c.status).join(', ')}`} style={{ fontSize: 10, color: live ? '#065f46' : '#1e40af', background: live ? '#d1fae5' : '#dbeafe', padding: '2px 6px', borderRadius: 20, fontWeight: 500 }}>
                {live ? '● Live' : drafted ? '○ Draft' : 'Shopify'}
              </span>
            );
          })()}
        </div>

        <Ring pct={score} size={40} stroke={4} />
      </div>

        {isManager && (
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 18, padding: '2px 6px', lineHeight: 1, flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); if (window.confirm(`Delete "${p.title || p.skuBase || 'this product'}"?`)) onDelete(p.id); }}
          >×</button>
        )}
      </div>
      {priceOpen && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: C.fa, borderTop: `1px solid ${C.e8}` }}>
          <span style={{ fontSize: 12, color: C.text2 }}>Set price:</span>
          <span style={{ fontSize: 12, color: C.muted }}>£</span>
          <input autoFocus style={{ ...inp({ width: 80, padding: '4px 8px', fontSize: 12 }) }} type="number" step="0.01"
            value={priceVal} onChange={e => setPriceVal(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setPriceOpen(false)} />
          <button style={btn('primary', { fontSize: 12, padding: '5px 14px' })} onClick={applyPrice}>Apply</button>
          <button style={btn('ghost', { fontSize: 12, padding: '5px 10px' })} onClick={() => setPriceOpen(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COLLECTION IMAGE UPLOAD MODAL
// ─────────────────────────────────────────────────────────────

function wordOverlap(a, b) {
  const wordsOf = s => new Set(s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean));
  const wa = wordsOf(a), wb = wordsOf(b);
  let shared = 0;
  wa.forEach(w => { if (wb.has(w)) shared++; });
  return shared / Math.max(wa.size, wb.size, 1);
}

export function CollectionImageUploadModal({ products, onAssign, onClose, studioMode = false }) {
  const { dropboxEnabled, googleDriveEnabled } = useTenantConfig();
  const [folderImages, setFolderImages] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [assignments, setAssignments] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [dbxBrowsing,  setDbxBrowsing]  = useState(false);
  const [dbxPath,      setDbxPath]      = useState('');
  const [dbxItems,     setDbxItems]     = useState([]);
  const [dbxLoading,   setDbxLoading]   = useState(false);
  const [dbxSelected,  setDbxSelected]  = useState(new Set());
  const [dbxMatchMap,  setDbxMatchMap]  = useState(new Map());
  const [dbxError,     setDbxError]     = useState('');
  const [dbxImporting,  setDbxImporting]  = useState(false);
  const [dbxImportProg, setDbxImportProg] = useState({ current: 0, total: 0 });
  const [dbxRecursive,    setDbxRecursive]    = useState(false);
  const [dbxFolderFilter, setDbxFolderFilter] = useState('');
  const [dbxFilter,       setDbxFilter]       = useState('all'); // 'all' | 'matched' | 'unmatched'
  const [urlInput,     setUrlInput]     = useState('');

  const parseDropboxUrl = (url) => {
    try {
      const homeIdx = url.indexOf('/home');
      if (homeIdx === -1) return null;
      const after = decodeURIComponent(url.slice(homeIdx + 5));
      return after || '';
    } catch { return null; }
  };
  const decodedPath = parseDropboxUrl(urlInput.trim());

  // Google Drive state
  const [gdBrowsing,   setGdBrowsing]   = useState(false);
  const [gdCrumbs,     setGdCrumbs]     = useState([]); // [{id, name}]
  const [gdItems,      setGdItems]      = useState([]);
  const [gdLoading,    setGdLoading]    = useState(false);
  const [gdSelected,   setGdSelected]   = useState(new Set());
  const [gdMatchMap,   setGdMatchMap]   = useState(new Map());
  const [gdError,      setGdError]      = useState('');
  const [gdImporting,  setGdImporting]  = useState(false);

  const applyFileList = (fileList) => {
    const labelOf = p => p.title || p.handle || p.skuBase || p.id;
    const map = {};
    const add = (sub, file) => { (map[sub] = map[sub] || []).push(file); };
    for (const file of fileList) {
      const rel = file.webkitRelativePath || '';
      const parts = rel.split('/').filter(Boolean);

      if (parts.length >= 3) {
        // Organised: root/ProductFolder/img.jpg → use subfolder name
        add(parts[parts.length - 2], file);
        continue;
      }
      // Flat: match the filename to products. A co-ord shot naming several products goes to
      // each of them — the same File in several groups, uploaded once (see handleUpload).
      const matches = matchProductsInFilename(file.name, products);
      if (matches.length) {
        matches.forEach(p => add(labelOf(p), file));
      } else {
        // Fallback: strip trailing frame number e.g. -7.4.2611
        const base = file.name.replace(/\.[^.]+$/, '');
        add(base.replace(/[-\s]*[\d.]+$/, '').trim() || base, file);
      }
    }

    for (const sub of Object.keys(map)) {
      map[sub].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    }

    // Auto-assign: group named after a product title will word-overlap perfectly
    const auto = {};
    for (const folder of Object.keys(map)) {
      let best = null, bestScore = 0;
      for (const p of products) {
        const score = wordOverlap(folder, p.title || p.handle || '');
        if (score > bestScore) { bestScore = score; best = p.id; }
      }
      auto[folder] = bestScore >= 0.4 ? best : '';
    }
    setFolderImages(map);
    setAssignments(auto);
    setUploadError('');
  };

  // Local folders can carry product video alongside the stills; Dropbox/Drive stay
  // image-only, since those proxy each file through a serverless function.
  const isMediaFile = f => f.type.startsWith('image/') || isVideoFile(f);

  // Dropbox/Drive browser label: every product the filename names ("Top + Shorts").
  const matchSummary = (name) => {
    const ms = matchProductsInFilename(name, products);
    return ms.length ? { id: ms[0].id, label: ms.map(p => p.title || p.handle || p.id).join(' + ') } : null;
  };

  const handleFolder = (e) => {
    const files = Array.from(e.target.files).filter(isMediaFile);
    applyFileList(files);
    e.target.value = '';
  };

  const IMAGE_EXTS = /\.(jpg|jpeg|png|webp|gif|tiff|tif)$/i;

  const dbxLoadFolder = async (path, recursive = dbxRecursive) => {
    setDbxLoading(true); setDbxError(''); setDbxSelected(new Set()); setDbxMatchMap(new Map());
    try {
      const res = await apiFetch(`/api/dropbox/list?path=${encodeURIComponent(path)}&recursive=${recursive}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to list folder');
      const entries = data.entries || [];
      setDbxItems(entries);
      setDbxPath(path);

      // Auto-select only files that match a product in this collection
      const matched = new Set();
      const matchMap = new Map();
      for (const item of entries) {
        if (item['.tag'] !== 'file' || !IMAGE_EXTS.test(item.name)) continue;
        const best = matchSummary(item.name);
        if (best) { matched.add(item.path_lower); matchMap.set(item.path_lower, best); }
      }
      setDbxSelected(matched);
      setDbxMatchMap(matchMap);
    } catch (e) { setDbxError(e.message); }
    finally { setDbxLoading(false); }
  };

  const dbxOpen = () => { setDbxBrowsing(true); dbxLoadFolder(''); };

  const dbxNavigate = (path) => dbxLoadFolder(path);

  const dbxToggle = (pathLower) => setDbxSelected(prev => {
    const n = new Set(prev);
    n.has(pathLower) ? n.delete(pathLower) : n.add(pathLower);
    return n;
  });

  const dbxImport = async () => {
    const folderTerm = dbxFolderFilter.trim().toLowerCase();
    const imageFiles = dbxItems.filter(e => {
      if (e['.tag'] !== 'file' || !IMAGE_EXTS.test(e.name)) return false;
      if (folderTerm) {
        const parts = e.path_lower.split('/').filter(Boolean);
        const parent = parts.length >= 2 ? parts[parts.length - 2] : '';
        if (!parent.includes(folderTerm)) return false;
      }
      return true;
    });
    const targets = dbxSelected.size > 0
      ? imageFiles.filter(e => dbxSelected.has(e.path_lower))
      : imageFiles;
    if (!targets.length) return;
    setDbxImporting(true); setDbxError(''); setDbxImportProg({ current: 0, total: targets.length });
    let completed = 0;
    try {
      const fetched = await runConcurrent(targets, 5, async (item) => {
        const res = await apiFetch(`/api/dropbox/link?path=${encodeURIComponent(item.path_lower)}`);
        const { link, error } = await res.json();
        if (error) throw new Error(error);
        const fileRes = await fetch(link);
        const blob = await fileRes.blob();
        setDbxImportProg({ current: ++completed, total: targets.length });
        return new File([blob], item.name, { type: blob.type || 'image/jpeg' });
      });
      applyFileList(fetched.filter(Boolean));
      setDbxBrowsing(false);
    } catch (e) { setDbxError('Import failed: ' + e.message); }
    finally { setDbxImporting(false); }
  };

  // ── Google Drive helpers ──────────────────────────────────────────
  const GD_IMAGE_TYPES = /^image\/(jpeg|png|webp|gif|tiff)/;

  const gdLoadFolder = async (folderId, name) => {
    setGdLoading(true); setGdError(''); setGdSelected(new Set()); setGdMatchMap(new Map());
    try {
      const res = await apiFetch(`/api/gdrive/list?folderId=${encodeURIComponent(folderId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to list folder');
      const files = data.files || [];
      setGdItems(files);
      setGdCrumbs(prev => {
        if (folderId === 'root') return [];
        const idx = prev.findIndex(c => c.id === folderId);
        if (idx >= 0) return prev.slice(0, idx + 1);
        return [...prev, { id: folderId, name: name || folderId }];
      });

      // Auto-match images to products
      const matched = new Set();
      const matchMap = new Map();
      for (const f of files) {
        if (f.mimeType === 'application/vnd.google-apps.folder') continue;
        if (!GD_IMAGE_TYPES.test(f.mimeType)) continue;
        const best = matchSummary(f.name);
        if (best) { matched.add(f.id); matchMap.set(f.id, best); }
      }
      setGdSelected(matched);
      setGdMatchMap(matchMap);
    } catch (e) { setGdError(e.message); }
    finally { setGdLoading(false); }
  };

  const gdOpen = () => { setGdBrowsing(true); gdLoadFolder('root'); };

  const gdToggle = (id) => setGdSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const gdImport = async () => {
    const imageFiles = gdItems.filter(f => GD_IMAGE_TYPES.test(f.mimeType));
    const targets = gdSelected.size > 0
      ? imageFiles.filter(f => gdSelected.has(f.id))
      : imageFiles;
    if (!targets.length) return;
    setGdImporting(true); setGdError('');
    try {
      const fetched = [];
      for (const file of targets) {
        const res = await apiFetch(`/api/gdrive/download?fileId=${encodeURIComponent(file.id)}`);
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Download failed'); }
        const blob = await res.blob();
        fetched.push(new File([blob], file.name, { type: blob.type || 'image/jpeg' }));
      }
      applyFileList(fetched);
      setGdBrowsing(false);
    } catch (e) { setGdError('Import failed: ' + e.message); }
    finally { setGdImporting(false); }
  };

  // ─────────────────────────────────────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map(i => i.webkitGetAsEntry?.()).filter(Boolean);

    if (entries.length) {
      const result = [];
      const walk = async (entry, relPath) => {
        if (entry.isFile) {
          await new Promise(res => entry.file(f => {
            Object.defineProperty(f, 'webkitRelativePath', { value: relPath, writable: false });
            if (isMediaFile(f)) result.push(f);
            res();
          }));
        } else if (entry.isDirectory) {
          await new Promise(res => {
            const reader = entry.createReader();
            const all = [];
            const read = () => reader.readEntries((batch) => {
              if (!batch.length) {
                Promise.all(all.map(child => walk(child, `${relPath}/${child.name}`))).then(() => res());
              } else { all.push(...batch); read(); }
            });
            read();
          });
        }
      };
      await Promise.all(entries.map(entry => walk(entry, entry.name)));
      applyFileList(result);
    } else {
      applyFileList(Array.from(e.dataTransfer.files || []).filter(isMediaFile));
    }
  };

  const handleUpload = async () => {
    const foldersToUpload = Object.entries(assignments).filter(([, pid]) => pid);
    if (!foldersToUpload.length) { setUploadError('No folders assigned to products. Select a product from the dropdown for each folder.'); return; }
    setUploading(true);
    setUploadError('');

    // Flatten all tasks and pre-init result maps
    const productImagesMap = {};
    const tasks = [];
    let taskIdx = 0;
    const nextPos = {};
    for (const [folder, pid] of foldersToUpload) {
      productImagesMap[pid] = productImagesMap[pid] || [];
      for (const file of folderImages[folder] || []) {
        nextPos[pid] = (nextPos[pid] ?? -1) + 1;
        tasks.push({ file, pid, folder, idx: taskIdx++, pos: nextPos[pid] });
      }
    }

    const totalFiles = tasks.length;
    let uploaded = 0;
    const errors = [];

    // A file shared by several products (a co-ord shot) is in several groups — upload it once
    // and give every product the same URL.
    const once = new Map();
    const uploadOnce = (file, pid, idx) => {
      if (!once.has(file)) once.set(file, (async () => {
        const path = `${pid}/${Date.now()}-${idx}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const bucket = mediaBucketFor(file);
        const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true, contentType: file.type || undefined });
        if (error) throw new Error(`${file.name}: ${error.message}`);
        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
        return { url: publicUrl, filename: file.name, ...(isVideoFile(file) ? { kind: 'video' } : {}) };
      })());
      return once.get(file);
    };
    const failed = new Set();
    await runConcurrent(tasks, 5, async ({ file, pid, folder, idx, pos }) => {
      setProgress(`Uploading ${++uploaded}/${totalFiles} (${folder})`);
      try {
        productImagesMap[pid][pos] = await uploadOnce(file, pid, idx);   // by position: keeps filename order
      } catch (err) {
        if (!failed.has(file)) { failed.add(file); errors.push(err.message); }
      }
    });

    // Fill gaps left by failures, and drop a shared file reaching one product via two groups.
    for (const pid of Object.keys(productImagesMap)) {
      const seen = new Set();
      productImagesMap[pid] = productImagesMap[pid].filter(m => m && !seen.has(m.url) && seen.add(m.url));
    }
    setUploading(false);
    const anyUploaded = Object.values(productImagesMap).some(arr => arr.length > 0);
    if (anyUploaded) onAssign(productImagesMap);
    if (errors.length) {
      setUploadError(`${errors.length} file(s) failed:\n${errors.join('\n')}`);
    } else {
      onClose();
    }
  };

  const folders = Object.keys(folderImages);

  if (uploading) {
    return (
      <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1a1a1a', color: C.white, borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.3)', fontSize: 13, whiteSpace: 'nowrap', minWidth: 320 }}>
        <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: C.white, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{progress || 'Uploading…'}</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ background: C.white, borderRadius: 14, padding: 28, width: 780, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', transition: 'all 0.2s', backgroundColor: isDragOver ? '#f9f5ff' : C.white, border: isDragOver ? '2px solid #a78bfa' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: F.display, fontSize: 18, color: C.text }}>Upload collection folder</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>×</button>
        </div>

        <p style={{ fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
          {isDragOver ? '📂 Drop your folder or files here to upload…' : 'Select the root collection folder, or individual images and videos. Subfolders — or, for loose files, filenames — are matched to products automatically by name. Review and adjust the assignments below before uploading.'}
        </p>

        {dropboxEnabled && (
          <div style={{ marginBottom: 20, padding: 14, background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.e8}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8, fontFamily: F.display }}>Dropbox URL → path</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  placeholder="Paste Dropbox URL, e.g. dropbox.com/home/Folder/Subfolder"
                  style={{ ...inp(), fontSize: 12, width: '100%', fontFamily: F.mono }}
                />
                {decodedPath !== null && urlInput.trim() && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.muted, fontFamily: F.mono, wordBreak: 'break-all', lineHeight: 1.5 }}>
                    <span style={{ color: '#6b7280' }}>Path: </span>
                    <span style={{ color: C.text }}>{decodedPath.replace(/^\//, '') || '/'}</span>
                  </div>
                )}
                {decodedPath === null && urlInput.trim() && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#b91c1c', fontFamily: F.display }}>No /home path found in URL</div>
                )}
              </div>
              <button
                disabled={!decodedPath && decodedPath !== ''}
                onClick={() => { setDbxBrowsing(true); dbxNavigate(decodedPath); }}
                style={{ ...btn('primary'), fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap', flexShrink: 0, opacity: (decodedPath === null && urlInput.trim()) ? 0.4 : 1 }}
              >Browse</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <label style={{ ...btn('secondary'), cursor: 'pointer', display: 'inline-block' }}>
            Select folder…
            <input type="file" accept="image/*,video/*" webkitdirectory="true" directory="" multiple style={{ display: 'none' }} onChange={handleFolder} />
          </label>
          {/* Individual files have no folder path, so they're matched to products by filename. */}
          <label style={{ ...btn('secondary'), cursor: 'pointer', display: 'inline-block' }}>
            Select files…
            <input type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={handleFolder} />
          </label>
          {dropboxEnabled && (
            <button
              onClick={dbxOpen}
              disabled={dbxImporting}
              style={{ ...btn('secondary'), display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 40 40" fill="none" style={{ flexShrink: 0 }}>
                <path d="M20 7.5L10 13.75L20 20L10 26.25L0 20L10 13.75L0 7.5L10 1.25L20 7.5Z" fill="#0061FF"/>
                <path d="M20 7.5L30 13.75L20 20L30 26.25L40 20L30 13.75L40 7.5L30 1.25L20 7.5Z" fill="#0061FF"/>
                <path d="M10 27.5L20 21.25L30 27.5L20 33.75L10 27.5Z" fill="#0061FF"/>
              </svg>
              Browse Dropbox
            </button>
          )}
          {googleDriveEnabled && (
            <button
              onClick={gdOpen}
              disabled={gdImporting}
              style={{ ...btn('secondary'), display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="16" height="16" viewBox="0 0 87.3 78" fill="none" style={{ flexShrink: 0 }}>
                <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066DA"/>
                <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L1.2 48.4A9 9 0 000 52.9h27.5z" fill="#00AC47"/>
                <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z" fill="#EA4335"/>
                <path d="M43.65 25L57.4 1.2A9.16 9.16 0 0053.9 0H33.4c-1.35 0-2.65.35-3.8 1z" fill="#00832D"/>
                <path d="M59.8 52.9h27.5a9 9 0 00-1.2-4.5L60.7 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25z" fill="#2684FC"/>
                <path d="M27.5 52.9l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h51.5c1.6 0 3.15-.45 4.5-1.2L59.8 52.9z" fill="#FFBA00"/>
              </svg>
              Browse Google Drive
            </button>
          )}
        </div>

        {dropboxEnabled && dbxBrowsing && (() => {
          const imageFiles = dbxItems.filter(e => e['.tag'] === 'file' && IMAGE_EXTS.test(e.name));
          const folderTerm = dbxFolderFilter.trim().toLowerCase();
          const visibleImageFiles = imageFiles.filter(e => {
            if (folderTerm) {
              const parts = e.path_lower.split('/').filter(Boolean);
              const parent = parts.length >= 2 ? parts[parts.length - 2] : '';
              if (!parent.includes(folderTerm)) return false;
            }
            return true;
          });
          const allSelected = visibleImageFiles.length > 0 && visibleImageFiles.every(e => dbxSelected.has(e.path_lower));
          const selectedVisible = visibleImageFiles.filter(e => dbxSelected.has(e.path_lower));
          const importCount = selectedVisible.length > 0 ? selectedVisible.length : visibleImageFiles.length;
          const pathSegs = dbxPath.split('/').filter(Boolean);
          return (
            <div style={{ border: `1px solid ${C.e8}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
              {/* Breadcrumb bar */}
              <div style={{ background: C.fa, borderBottom: `1px solid ${C.e8}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <span onClick={() => dbxNavigate('')} style={{ cursor: 'pointer', color: C.accent, fontSize: 12, fontFamily: F.display, flexShrink: 0 }}>Dropbox</span>
                    {pathSegs.map((seg, i) => {
                      const segPath = '/' + pathSegs.slice(0, i + 1).join('/');
                      return (
                        <span key={segPath} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: i < pathSegs.length - 1 ? 1 : 0 }}>
                          <span style={{ color: C.muted, fontSize: 12, flexShrink: 0 }}>/</span>
                          <span onClick={() => dbxNavigate(segPath)} style={{ cursor: 'pointer', fontSize: 12, fontFamily: F.display, color: i === pathSegs.length - 1 ? C.text : C.accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg}</span>
                        </span>
                      );
                    })}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, color: C.muted, fontFamily: F.display, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <input type="checkbox" checked={dbxRecursive} onChange={e => { setDbxRecursive(e.target.checked); if (!e.target.checked) setDbxFolderFilter(''); dbxLoadFolder(dbxPath, e.target.checked); }} style={{ margin: 0 }} />
                    Search subfolders
                  </label>
                  <button onClick={() => setDbxBrowsing(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0 }}>✕</button>
                </div>
                {dbxRecursive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderTop: `1px solid ${C.e8}` }}>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: F.display, whiteSpace: 'nowrap' }}>Filter by folder name:</span>
                    <input
                      value={dbxFolderFilter}
                      onChange={e => setDbxFolderFilter(e.target.value)}
                      placeholder="e.g. fronts (leave blank to show all)"
                      style={{ ...inp(), fontSize: 11, fontFamily: F.mono, padding: '3px 8px', flex: 1 }}
                    />
                    {dbxFolderFilter && <button onClick={() => setDbxFolderFilter('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: C.muted, padding: 0, fontFamily: F.display }}>Clear</button>}
                  </div>
                )}
              </div>
              {/* Direct path input — hidden in recursive mode */}
              {!dbxRecursive && <div style={{ display: 'flex', gap: 6, padding: '7px 10px', borderBottom: `1px solid ${C.e8}`, background: '#fff' }}>
                <input
                  placeholder="Jump to path, e.g. /Season 2025/Campaign"
                  defaultValue={dbxPath}
                  key={dbxPath}
                  onKeyDown={e => { if (e.key === 'Enter') { const v = e.target.value.trim(); dbxNavigate(v.startsWith('/') || v === '' ? v : '/' + v); } }}
                  style={{ ...inp(), flex: 1, fontSize: 12, fontFamily: F.mono, padding: '4px 8px' }}
                />
                <button onClick={e => { const inp2 = e.currentTarget.previousSibling; const v = inp2.value.trim(); dbxNavigate(v.startsWith('/') || v === '' ? v : '/' + v); }}
                  style={{ ...btn('secondary'), fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>Go</button>
              </div>}

              {/* Import progress overlay */}
              {dbxImporting && (
                <div style={{ padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 28, height: 28, border: '3px solid #dbeafe', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <div style={{ fontSize: 14, fontFamily: F.display, color: C.text, fontWeight: 500 }}>
                    Fetching {dbxImportProg.current} of {dbxImportProg.total} image{dbxImportProg.total !== 1 ? 's' : ''}…
                  </div>
                  <div style={{ width: '100%', maxWidth: 280, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#2563eb', borderRadius: 2, width: `${dbxImportProg.total ? (dbxImportProg.current / dbxImportProg.total) * 100 : 0}%`, transition: 'width 0.2s ease' }} />
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, fontFamily: F.display }}>This may take a moment for large batches</div>
                </div>
              )}

              {/* File list */}
              {!dbxImporting && dbxLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, fontFamily: F.display }}>Loading…</div>
              ) : dbxError ? (
                <div style={{ padding: 14, color: '#b91c1c', fontSize: 12, fontFamily: F.display }}>{dbxError}</div>
              ) : (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {dbxPath && !dbxRecursive && (
                    <div onClick={() => dbxNavigate(pathSegs.length > 1 ? '/' + pathSegs.slice(0, -1).join('/') : '')}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: C.muted, borderBottom: `1px solid ${C.e8}`, fontFamily: F.display }}>
                      ← Up
                    </div>
                  )}
                  {dbxItems.filter(item => {
                    if (dbxRecursive && item['.tag'] === 'folder') return false;
                    if (item['.tag'] === 'folder') return true;
                    if (!IMAGE_EXTS.test(item.name)) return !dbxRecursive;
                    if (dbxRecursive && dbxFolderFilter.trim()) {
                      const parts = item.path_lower.split('/').filter(Boolean);
                      const parentFolder = parts.length >= 2 ? parts[parts.length - 2] : '';
                      if (!parentFolder.includes(dbxFolderFilter.trim().toLowerCase())) return false;
                    }
                    if (dbxFilter === 'matched') return dbxMatchMap.has(item.path_lower);
                    if (dbxFilter === 'unmatched') return !dbxMatchMap.has(item.path_lower);
                    return true;
                  }).map(item => {
                    const isFolder = item['.tag'] === 'folder';
                    const isImage = !isFolder && IMAGE_EXTS.test(item.name);
                    const checked = dbxSelected.has(item.path_lower);
                    const match = dbxMatchMap.get(item.path_lower);
                    return (
                      <div key={item.path_lower}
                        onClick={() => isFolder ? dbxNavigate(item.path_lower) : (isImage && dbxToggle(item.path_lower))}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', cursor: isFolder || isImage ? 'pointer' : 'default', borderBottom: `1px solid ${C.e8}`, background: checked ? '#eff6ff' : 'transparent', fontFamily: F.display }}>
                        {!isFolder && isImage && (
                          <input type="checkbox" checked={checked} onChange={() => dbxToggle(item.path_lower)} onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }} />
                        )}
                        {isFolder && <span style={{ width: 16, flexShrink: 0 }} />}
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{isFolder ? '📁' : (isImage ? '🖼' : '📄')}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                          {dbxRecursive && !isFolder && item.path_lower && (() => {
                            const rel = item.path_lower.slice(dbxPath.length).replace(/\/[^/]+$/, '').replace(/^\//, '');
                            return rel ? <div style={{ fontSize: 10, color: C.muted, fontFamily: F.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{rel}</div> : null;
                          })()}
                        </div>
                        {isImage && (
                          <span style={{ fontSize: 11, whiteSpace: 'nowrap', padding: '2px 6px', borderRadius: 4, background: match ? '#dcfce7' : '#f3f4f6', color: match ? '#15803d' : C.muted, flexShrink: 0 }}>
                            {match ? match.label : 'no match'}
                          </span>
                        )}
                        {isFolder && <span style={{ fontSize: 12, color: C.muted }}>›</span>}
                      </div>
                    );
                  })}
                  {dbxItems.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, fontFamily: F.display }}>This folder is empty</div>
                  )}
                </div>
              )}

              {/* Footer actions */}
              {!dbxImporting && !dbxLoading && !dbxError && imageFiles.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: `1px solid ${C.e8}`, background: C.fa }}>
                  <button onClick={() => setDbxSelected(allSelected ? new Set() : new Set(visibleImageFiles.map(e => e.path_lower)))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.accent, padding: 0, fontFamily: F.display }}>
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                  <span style={{ fontSize: 12, color: C.muted }}>
                    {selectedVisible.length} selected · {visibleImageFiles.filter(e => dbxMatchMap.has(e.path_lower)).length} matched · {visibleImageFiles.length} shown{folderTerm || dbxFilter !== 'all' ? ` of ${imageFiles.length}` : ''}
                  </span>
                  <div style={{ display: 'flex', gap: 2, background: C.e8, borderRadius: 6, padding: 2 }}>
                    {['all', 'matched', 'unmatched'].map(f => (
                      <button key={f} onClick={() => setDbxFilter(f)}
                        style={{ background: dbxFilter === f ? C.white : 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, padding: '3px 8px', borderRadius: 4, color: dbxFilter === f ? C.text : C.muted, fontFamily: F.display, fontWeight: dbxFilter === f ? 600 : 400, boxShadow: dbxFilter === f ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.1s' }}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                  <button onClick={dbxImport} disabled={dbxImporting || dbxSelected.size === 0} style={{ ...btn('primary'), fontSize: 12, padding: '5px 14px' }}>
                    {dbxImporting ? 'Importing…' : `Import ${importCount} image${importCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
              {dbxError && !dbxLoading && (
                <div style={{ padding: '8px 12px', color: '#b91c1c', fontSize: 12, borderTop: `1px solid ${C.e8}`, fontFamily: F.display }}>{dbxError}</div>
              )}
            </div>
          );
        })()}

        {/* Google Drive browser */}
        {googleDriveEnabled && gdBrowsing && (() => {
          const imageFiles = gdItems.filter(f => GD_IMAGE_TYPES.test(f.mimeType));
          const allSelected = imageFiles.length > 0 && imageFiles.every(f => gdSelected.has(f.id));
          const importCount = gdSelected.size > 0 ? gdSelected.size : imageFiles.length;
          return (
            <div style={{ border: `1px solid ${C.e8}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
              {/* Breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: C.fa, borderBottom: `1px solid ${C.e8}` }}>
                <span onClick={() => gdLoadFolder('root')} style={{ cursor: 'pointer', color: C.accent, fontSize: 12, fontFamily: F.display }}>My Drive</span>
                {gdCrumbs.map((crumb, i) => (
                  <span key={crumb.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: C.muted, fontSize: 12 }}>/</span>
                    <span onClick={() => gdLoadFolder(crumb.id, crumb.name)} style={{ cursor: 'pointer', fontSize: 12, fontFamily: F.display, color: i === gdCrumbs.length - 1 ? C.text : C.accent }}>{crumb.name}</span>
                  </span>
                ))}
                <span style={{ flex: 1 }} />
                <button onClick={() => setGdBrowsing(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, lineHeight: 1, padding: 2 }}>✕</button>
              </div>

              {/* File list */}
              {gdLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, fontFamily: F.display }}>Loading…</div>
              ) : gdError ? (
                <div style={{ padding: 14, color: '#b91c1c', fontSize: 12, fontFamily: F.display }}>{gdError}</div>
              ) : (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {gdCrumbs.length > 0 && (
                    <div onClick={() => { const prev = gdCrumbs[gdCrumbs.length - 2]; gdLoadFolder(prev ? prev.id : 'root', prev?.name); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: C.muted, borderBottom: `1px solid ${C.e8}`, fontFamily: F.display }}>
                      ← Up
                    </div>
                  )}
                  {gdItems.map(file => {
                    const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
                    const isImage  = GD_IMAGE_TYPES.test(file.mimeType);
                    const checked  = gdSelected.has(file.id);
                    const match    = gdMatchMap.get(file.id);
                    return (
                      <div key={file.id}
                        onClick={() => isFolder ? gdLoadFolder(file.id, file.name) : (isImage && gdToggle(file.id))}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', cursor: isFolder || isImage ? 'pointer' : 'default', borderBottom: `1px solid ${C.e8}`, background: checked ? '#eff6ff' : 'transparent', fontFamily: F.display }}>
                        {!isFolder && isImage && (
                          <input type="checkbox" checked={checked} onChange={() => gdToggle(file.id)} onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }} />
                        )}
                        {(isFolder || !isImage) && <span style={{ width: 16, flexShrink: 0 }} />}
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{isFolder ? '📁' : (isImage ? '🖼' : '📄')}</span>
                        <span style={{ fontSize: 13, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        {isImage && (
                          <span style={{ fontSize: 11, whiteSpace: 'nowrap', padding: '2px 6px', borderRadius: 4, background: match ? '#dcfce7' : '#f3f4f6', color: match ? '#15803d' : C.muted, flexShrink: 0 }}>
                            {match ? match.label : 'no match'}
                          </span>
                        )}
                        {isFolder && <span style={{ fontSize: 12, color: C.muted }}>›</span>}
                      </div>
                    );
                  })}
                  {gdItems.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, fontFamily: F.display }}>This folder is empty</div>
                  )}
                </div>
              )}

              {/* Footer */}
              {!gdLoading && !gdError && imageFiles.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: `1px solid ${C.e8}`, background: C.fa }}>
                  <button onClick={() => setGdSelected(allSelected ? new Set() : new Set(imageFiles.map(f => f.id)))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.accent, padding: 0, fontFamily: F.display }}>
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                  <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>
                    {gdSelected.size} selected · {gdMatchMap.size} matched to products · {imageFiles.length} total
                  </span>
                  <button onClick={gdImport} disabled={gdImporting || gdSelected.size === 0} style={{ ...btn('primary'), fontSize: 12, padding: '5px 14px' }}>
                    {gdImporting ? 'Importing…' : `Import ${importCount} image${importCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
              {gdError && !gdLoading && (
                <div style={{ padding: '8px 12px', color: '#b91c1c', fontSize: 12, borderTop: `1px solid ${C.e8}`, fontFamily: F.display }}>{gdError}</div>
              )}
            </div>
          );
        })()}

        {folders.length > 0 && (
          <>
            <div style={{ fontFamily: F.display, fontSize: 13, color: C.text, marginBottom: 8 }}>{folders.length} subfolders found</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {folders.map(folder => {
                const assignedId = assignments[folder] || '';
                const imgCount = folderImages[folder].length;
                const shared = folderImages[folder].filter(f => folders.some(o => o !== folder && folderImages[o].includes(f))).length;
                const preview = folderImages[folder].slice(0, 3).map(f => ({ src: URL.createObjectURL(f), video: isVideoFile(f) }));
                return (
                  <div key={folder} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: C.fa, border: `1px solid ${assignedId ? C.e8 : '#fca5a5'}`, borderRadius: 10 }}>
                    {/* Previews */}
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {preview.map(({ src, video }, i) => video
                        ? <video key={i} src={src} muted preload="metadata" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: `1px solid ${C.e8}` }} />
                        : <img key={i} src={src} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: `1px solid ${C.e8}` }} />
                      )}
                    </div>
                    {/* Folder info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{imgCount} file{imgCount !== 1 ? 's' : ''}{shared > 0 ? ` · ${shared} shared with other products` : ''}</div>
                    </div>
                    {/* Product assignment */}
                    <select value={assignedId}
                      onChange={e => setAssignments(prev => ({ ...prev, [folder]: e.target.value }))}
                      style={{ ...inp({ minWidth: 200, maxWidth: 260 }), fontSize: 12 }}>
                      <option value="">Skip this folder</option>
                      {products.map(prod => (
                        <option key={prod.id} value={prod.id}>{prod.title || prod.handle || prod.skuBase || prod.id}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            {uploadError && (
              <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#b91c1c', whiteSpace: 'pre-wrap' }}>
                {uploadError}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleUpload} disabled={uploading} style={btn('primary')}>
                {uploading ? progress || 'Uploading…' : `Upload ${Object.values(assignments).filter(Boolean).length} folder(s)`}
              </button>
              {!uploading && (
                <span style={{ fontSize: 12, color: C.muted }}>{Object.values(assignments).filter(Boolean).length} of {folders.length} folders assigned</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────

export function Dashboard({ collection, roles, onEditProduct, onUpdateProducts, onDuplicate }) {
  const tenantConfig = useTenantConfig();
  const { deptIds } = tenantConfig;
  const [search, setSearch]           = useState('');
  const [filter, setFilter]           = useState('all');
  const [showImport, setShowImport]         = useState(false);
  const [showDuplicate, setShowDuplicate]   = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [undoDelete, setUndoDelete]   = useState(null); // { snapshot: [...], label: '' }
  const [genProgress, setGenProgress]     = useState(null); // null | { current, total, label }
  const [presetResult, setPresetResult]   = useState(null); // null | { applied, skipped }
  const undoTimer = useRef(null);
  useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current); }, []);
  const isManager = roles?.includes('manager') ?? false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const products  = useMemo(() => collection?.products || [], [collection?.products]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return products.filter(p => {
      const matchSearch = !q || [p.title, p.skuBase, p.handle].some(s => s?.toLowerCase().includes(q));
      const allApproved = deptIds.every(d => p.sectionStatus[d] === 'approved');
      const hasReview   = deptIds.some(d => p.sectionStatus[d] === 'review');
      const matchFilter =
        filter === 'all'    ? true :
        filter === 'ready'  ? allApproved :
        filter === 'review' ? hasReview : true;
      return matchSearch && matchFilter;
    });
  }, [products, search, filter, deptIds]);

  const totalApproved = products.filter(p => deptIds.every(d => p.sectionStatus[d] === 'approved')).length;
  const totalReview   = products.filter(p => deptIds.some(d => p.sectionStatus[d] === 'review')).length;
  const avgScore      = products.length ? Math.round(products.reduce((s, p) => s + calcOverallScore(p, tenantConfig), 0) / products.length) : 0;

  const triggerUndo = (removed, label) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoDelete({ snapshot: products, label });
    onUpdateProducts(removed);
    undoTimer.current = setTimeout(() => setUndoDelete(null), 8000);
  };

  const addProduct    = () => onUpdateProducts([...products, makeProduct({}, tenantConfig)]);
  const deleteProduct = id => {
    const p = products.find(pr => pr.id === id);
    triggerUndo(products.filter(pr => pr.id !== id), p?.title || 'product');
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) { const n = new Set(selected); filtered.forEach(p => n.delete(p.id)); setSelected(n); }
    else { const n = new Set(selected); filtered.forEach(p => n.add(p.id)); setSelected(n); }
  };
  const toggleSelect = (id, val) => { const n = new Set(selected); val ? n.add(id) : n.delete(id); setSelected(n); };
  const deleteSelected = () => {
    const count = selected.size;
    triggerUndo(products.filter(p => !selected.has(p.id)), `${count} product${count > 1 ? 's' : ''}`);
    setSelected(new Set());
  };

  const generateDescriptions = async () => {
    const targets = products.filter(p => selected.has(p.id));
    if (!targets.length) return;
    setGenProgress({ current: 0, total: targets.length, label: '' });
    setSelected(new Set());
    const updated = [...products];
    for (let i = 0; i < targets.length; i++) {
      const p = targets[i];
      setGenProgress({ current: i + 1, total: targets.length, label: p.title || p.skuBase || '' });
      try {
        const res = await apiFetch('/api/generate-description', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subdomain: tenantConfig.subdomain, product: p }),
        });
        const { description } = await res.json();
        if (description) {
          const idx = updated.findIndex(pr => pr.id === p.id);
          if (idx !== -1) updated[idx] = { ...updated[idx], bodyHtml: description, updatedAt: new Date().toISOString() };
        }
      } catch (_) { /* skip failed product, continue */ }
    }
    onUpdateProducts(updated);
    setGenProgress(null);
  };

  const applyVariantPresets = () => {
    const presets = tenantConfig?.variantPresets || [];
    if (!presets.length) return;
    let applied = 0, skipped = 0;
    const updated = products.map(p => {
      if (!selected.has(p.id)) return p;
      const productType = (p.type || '').toLowerCase();
      const preset = presets.find(pr =>
        !pr.productTypes?.length || pr.productTypes.some(t => t.toLowerCase() === productType)
      );
      if (!preset?.opt1Values?.length) { skipped++; return p; }
      const base = p.skuBase || 'SKU';
      const defaults = { price: p.basePrice || '', compareAtPrice: '', costPerItem: '', barcode: '', grams: 200, hsCode: '', coo: '' };
      const skuSuffix1 = (i) => (preset.opt1Skus?.[i] ?? preset.opt1Values[i]).replace(/\s+/g, '');
      const skuSuffix2 = (i) => (preset.opt2Skus?.[i] ?? preset.opt2Values[i]).replace(/\s+/g, '');
      const hasTwoOpts = preset.opt2Name && preset.opt1Values.length && preset.opt2Values?.length;
      let variants;
      if (hasTwoOpts) {
        variants = [];
        preset.opt1Values.forEach((v1, i1) => preset.opt2Values.forEach((v2, i2) => {
          variants.push({ id: uid(), sku: `${base}-${skuSuffix1(i1)}${skuSuffix2(i2)}`, opt1Name: preset.opt1Name, opt1Val: v1, opt2Name: preset.opt2Name, opt2Val: v2, ...defaults });
        }));
      } else {
        variants = preset.opt1Values.map((val, i) => ({
          id: uid(), sku: `${base}-${skuSuffix1(i)}`,
          opt1Name: preset.opt1Name || 'Size', opt1Val: val, opt2Name: '', opt2Val: '', ...defaults,
        }));
      }
      applied++;
      return { ...p, variantType: hasTwoOpts ? TWO_DIM_VARIANT_TYPE : 'size', variants, updatedAt: new Date().toISOString() };
    });
    onUpdateProducts(updated);
    setSelected(new Set());
    setPresetResult({ applied, skipped });
    setTimeout(() => setPresetResult(null), 6000);
  };

  const statCard = (label, val) => (
    <div style={{ background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontFamily: F.display, fontSize: 30, color: C.text }}>{val}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Bulk generate progress toast */}
      {genProgress && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1a1a1a', color: C.white, borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.3)', fontSize: 13, whiteSpace: 'nowrap', minWidth: 320 }}>
          <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: C.white, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ✨ Generating {genProgress.current}/{genProgress.total}{genProgress.label ? ` (${genProgress.label})` : ''}
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Preset result toast */}
      {presetResult && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1a1a1a', color: C.white, borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', fontSize: 13, whiteSpace: 'nowrap' }}>
          <span>
            Applied variants to <strong>{presetResult.applied}</strong> product{presetResult.applied !== 1 ? 's' : ''}
            {presetResult.skipped > 0 && <>, <strong>{presetResult.skipped}</strong> skipped (no matching preset)</>}
          </span>
          <button onClick={() => setPresetResult(null)} style={{ background: 'transparent', color: '#999', border: 'none', fontSize: 16, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Undo delete toast */}
      {undoDelete && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1a1a1a', color: C.white, borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', fontSize: 13, whiteSpace: 'nowrap' }}>
          <span>Deleted <strong>{undoDelete.label}</strong></span>
          <button onClick={() => { if (undoTimer.current) clearTimeout(undoTimer.current); onUpdateProducts(undoDelete.snapshot); setUndoDelete(null); }}
            style={{ background: C.white, color: '#1a1a1a', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Undo
          </button>
          <button onClick={() => { if (undoTimer.current) clearTimeout(undoTimer.current); setUndoDelete(null); }}
            style={{ background: 'transparent', color: '#999', border: 'none', fontSize: 16, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>
            ×
          </button>
        </div>
      )}
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {statCard('Products',      products.length)}
        {statCard('Export ready',  totalApproved)}
        {statCard('Needs review',  totalReview)}
        {statCard('Avg complete',  `${avgScore}%`)}
      </div>

      {/* Dept grid (manager only) */}
      {isManager && products.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 24 }}>
          {deptIds.map(d => {
            const deptReview   = products.filter(p => p.sectionStatus[d] === 'review').length;
            const deptApproved = products.filter(p => p.sectionStatus[d] === 'approved').length;
            const avgFill      = products.length
              ? Math.round(products.reduce((s, p) => s + calcSectionScore(p, d, tenantConfig), 0) / products.length)
              : 0;
            return (
              <div key={d} style={{ background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{d}</div>
                <div style={{ fontFamily: F.display, fontSize: 22, color: C.text }}>{avgFill}%</div>
                {deptReview > 0 && (
                  <div style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', padding: '2px 6px', borderRadius: 10, display: 'inline-block', marginTop: 6 }}>
                    {deptReview} to review
                  </div>
                )}
                {deptReview === 0 && deptApproved > 0 && (
                  <div style={{ fontSize: 10, color: '#065f46', marginTop: 4 }}>{deptApproved} approved</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {isManager && filtered.length > 0 && (
          <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
            title="Select all" style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
        )}
        <input style={{ ...inp({ flex: 1, maxWidth: 280 }) }} value={search}
          onChange={e => setSearch(e.target.value)} placeholder="Search products…" />
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'ready', 'review'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              ...btn('ghost', { fontSize: 12, padding: '6px 12px', border: `1px solid ${f === filter ? C.text : C.e8}`, borderRadius: 6 }),
              background: f === filter ? C.text : C.white,
              color:      f === filter ? C.white : C.text2,
            }}>
              {f === 'all' ? 'All' : f === 'ready' ? 'Ready' : 'Review'}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {isManager && selected.size > 0 && (
            <>
              {(tenantConfig?.variantPresets?.length > 0) && (
                <button style={btn('secondary', { fontSize: 12 })} onClick={applyVariantPresets}>
                  Apply variant presets ({selected.size})
                </button>
              )}
              <button style={btn('secondary', { fontSize: 12 })} onClick={generateDescriptions}>
                ✨ Generate descriptions ({selected.size})
              </button>
              <button style={btn('danger', { fontSize: 12 })} onClick={deleteSelected}>
                Delete {selected.size} selected
              </button>
            </>
          )}
          {isManager && (
            <>
              <button style={btn('secondary')} onClick={() => setShowDuplicate(true)}>Duplicate collection</button>
              <button style={btn('secondary')} onClick={() => setShowImport(true)}>Import CSV</button>
              <button style={btn('secondary')} onClick={() => setShowImageUpload(true)}>Upload images</button>
              <button style={btn('primary')} onClick={addProduct}>+ Add product</button>
            </>
          )}
        </div>
      </div>

      {/* Product list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14, background: C.fa, borderRadius: 10, border: `1px solid ${C.e8}` }}>
            {products.length === 0
              ? 'No products yet. Add your first product to get started.'
              : 'No products match your search or filter.'}
          </div>
        ) : buildProductGroups(filtered, tenantConfig).map(({ key, items }) => {
          if (!key || items.length === 1) {
            const p = items[0];
            return (
              <ProductRow key={p.id} p={p} isManager={isManager}
                onEdit={onEditProduct} onDelete={deleteProduct}
                selected={selected.has(p.id)} onSelect={toggleSelect}
                onPriceChange={(id, price) => onUpdateProducts(products.map(pr => pr.id === id ? { ...pr, basePrice: price, variants: (pr.variants || []).map(v => ({ ...v, price })), updatedAt: new Date().toISOString() } : pr))} />
            );
          }
          return (
            <ProductGroup key={key} groupKey={key} items={items} isManager={isManager}
              groupLabel={tenantConfig?.groupingLabel || 'colour'}
              onEdit={onEditProduct} onDelete={deleteProduct}
              selected={selected} onSelect={toggleSelect}
              onSyncPrice={(ids, price) => {
                const updated = products.map(p =>
                  ids.includes(p.id)
                    ? { ...p, basePrice: price, variants: (p.variants || []).map(v => ({ ...v, price })), updatedAt: new Date().toISOString() }
                    : p
                );
                onUpdateProducts(updated);
              }}
            />
          );
        })}
      </div>

      {showImport && (
        <ImportModal
          existingProducts={products}
          onImport={prods => {
            // Merge by handle, then skuBase — update existing, append truly new
            const idxByHandle = new Map(products.map((p, i) => p.handle ? [p.handle, i] : null).filter(Boolean));
            const idxBySku    = new Map(products.map((p, i) => p.skuBase ? [p.skuBase, i] : null).filter(Boolean));
            const merged = [...products];
            prods.forEach(p => {
              if (p.handle && idxByHandle.has(p.handle)) {
                merged[idxByHandle.get(p.handle)] = p;
              } else if (p.skuBase && idxBySku.has(p.skuBase)) {
                merged[idxBySku.get(p.skuBase)] = p;
              } else {
                merged.push(p);
              }
            });
            onUpdateProducts(merged);
          }}
          onClose={() => setShowImport(false)}
        />
      )}
      {showDuplicate && (
        <DuplicateModal collection={collection} onDuplicate={onDuplicate} onClose={() => setShowDuplicate(false)} />
      )}
      {showImageUpload && (
        <CollectionImageUploadModal
          products={products}
          onAssign={(productImagesMap) => {
            const updated = products.map(prod => {
              const newImgs = productImagesMap[prod.id];
              if (!newImgs || newImgs.length === 0) return prod;
              const merged = [...(prod.images || []), ...newImgs];
              return { ...prod, images: merged, ...syncImagesFields(merged), updatedAt: new Date().toISOString() };
            });
            onUpdateProducts(updated);
            setShowImageUpload(false);
          }}
          onClose={() => setShowImageUpload(false)}
        />
      )}
    </div>
  );
}
